import React, { useState, useEffect } from 'react';
import './ParentDashboard.css';
import DashboardHeader from '../components/DashboardHeader';
import ParentNavigationBar from '../components/ParentNavigationBar';
import SignalementIncident from '../components/SignalementIncident';
import { 
  BellIcon, 
  CalendarIcon, 
  BookIcon, 
  SchoolIcon, 
  ClockIcon, 
  ChartIcon, 
  AlertIcon, 
  CheckCircleIcon, 
  ShieldIcon,
  MailIcon,
  FileTextIcon
} from '../components/Icons';
import { toSentenceCase } from '../utils/textUtils';

function StudentDashboard() {
  const [selectedTrimester, setSelectedTrimester] = useState('3');
  const [studentInfo, setStudentInfo] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notes, setNotes] = useState([]);
  const [bulletins, setBulletins] = useState([]);
  const [stats, setStats] = useState({
    absences: 0,
    retards: 0,
    moyenne: 0,
    notes_faibles: 0
  });
  const [loading, setLoading] = useState(true);
  const [showSignalement, setShowSignalement] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  useEffect(() => {
    fetchStudentData();
  }, []);

  useEffect(() => {
    if (studentInfo?.id) {
      fetchNotifications();
      fetchBulletins();
      fetchNotes().then((notesData) => {
        fetchStats(notesData);
      });
    }
  }, [selectedTrimester, studentInfo]);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      const userEmail = localStorage.getItem('user_email') || 'sedjro153@gmail.com';
      console.log('📧 Email utilisé:', userEmail);
      
      const response = await fetch(`http://127.0.0.1:8000/api/v1/etudiant/me?email=${userEmail}`);
      const data = await response.json();
      
      console.log('👤 Données étudiant reçues:', data);
      
      if (response.ok && data.etudiant) {
        setStudentInfo(data.etudiant);
        console.log('✅ Étudiant défini:', data.etudiant);
      } else {
        console.error('❌ Erreur API:', data);
      }
      setLoading(false);
    } catch (error) {
      console.error('❌ Erreur fetch:', error);
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      if (!studentInfo?.id) return;
      
      const response = await fetch(`http://127.0.0.1:8000/api/v1/admin/notifications/etudiant/${studentInfo.id}/non-lues`);
      const data = await response.json();
      
      console.log('📢 Notifications reçues:', data);
      
      if (response.ok && data.notifications) {
        // Transformer les données pour correspondre au format attendu
        const transformedNotifications = data.notifications.map(notif => {
          const parsedData = notif.data ? JSON.parse(notif.data) : {};
          return {
            id: notif.id,
            title: notif.titre,
            message: notif.message,
            type: notif.type,
            date: notif.created_at,
            read: notif.lu,
            priority: parsedData.priority || 'normale'
          };
        });
        
        setNotifications(transformedNotifications);
        console.log('✅ Notifications transformées:', transformedNotifications);
      } else {
        console.log('⚠️ Pas de notifications ou erreur API:', data);
        setNotifications([]);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des notifications:', error);
      setNotifications([]);
    }
  };

  const fetchNotes = async () => {
    try {
      console.log('📝 Récupération notes pour étudiant ID:', studentInfo.id);
      const response = await fetch(`http://127.0.0.1:8000/api/v1/admin/notes/etudiant/${studentInfo.id}`);
      const data = await response.json();
      
      console.log('📊 Notes reçues:', data);
      
      if (response.ok && Array.isArray(data)) {
        // Filtrer par trimestre
        const notesFiltered = data.filter(n => n.trimestre === selectedTrimester && n.annee_scolaire === '2025-2026');
        console.log(`✅ Notes filtrées (trimestre ${selectedTrimester}):`, notesFiltered);
        setNotes(notesFiltered);
        return notesFiltered; // Retourner les notes pour les utiliser dans fetchStats
      } else {
        console.error('❌ Erreur notes:', data);
        return [];
      }
    } catch (error) {
      console.error('❌ Erreur récupération notes:', error);
      return [];
    }
  };

  const fetchBulletins = async () => {
    try {
      const response = await fetch(`http://127.0.0.1:8000/api/v1/bulletins/etudiant/${studentInfo.id}`);
      const data = await response.json();
      
      if (response.ok && data.success) {
        setBulletins(data.data || []);
      }
    } catch (error) {
      console.error('Erreur récupération bulletins:', error);
    }
  };

  const fetchStats = async (notesData = []) => {
    try {
      if (!studentInfo?.id) {
        console.log('⚠️ Pas d\'ID étudiant pour fetchStats');
        return;
      }

      console.log('📊 Récupération stats pour étudiant ID:', studentInfo.id);

      // Récupérer le bulletin stocké pour la moyenne
      const bulletinResponse = await fetch(
        `http://127.0.0.1:8000/api/v1/bulletins/etudiant/${studentInfo.id}`
      );
      const bulletinData = await bulletinResponse.json();
      console.log('📄 Bulletins data:', bulletinData);

      // Trouver le bulletin du trimestre sélectionné
      const bulletinTrimestre = bulletinData.data?.find(
        b => b.trimestre === selectedTrimester && b.annee_scolaire === '2025-2026'
      );
      
      const moyenne = bulletinTrimestre?.moyenne_generale || 0;
      console.log(`📊 Moyenne du bulletin T${selectedTrimester}:`, moyenne);

      // Récupérer les absences
      const absencesResponse = await fetch(
        `http://127.0.0.1:8000/api/v1/admin/absences?etudiant_id=${studentInfo.id}&trimestre=${selectedTrimester}`
      );
      const absencesData = await absencesResponse.json();
      console.log('🚫 Absences data:', absencesData);

      const totalAbsences = absencesData.data?.filter(a => a.type === 'absence').length || 0;
      const totalRetards = absencesData.data?.filter(a => a.type === 'retard').length || 0;
      
      // Compter les notes faibles (< 10) dans les notes du trimestre
      const notesFaibles = notesData.filter(n => n.note < 10).length;

      const newStats = {
        absences: totalAbsences,
        retards: totalRetards,
        moyenne: moyenne,
        notes_faibles: notesFaibles
      };
      
      console.log('✅ Stats calculées:', newStats);
      setStats(newStats);
    } catch (error) {
      console.error('❌ Erreur récupération stats:', error);
      // Garder les valeurs par défaut
      setStats({
        absences: 0,
        retards: 0,
        moyenne: 0,
        notes_faibles: 0
      });
    }
  };

  const handleDownloadBulletin = async () => {
    try {
      // Vérifier qu'un bulletin est disponible pour ce trimestre
      const bulletin = bulletins.find(b => 
        b.trimestre === selectedTrimester && 
        b.annee_scolaire === '2025-2026' && 
        b.statut === 'genere'
      );
      
      if (!bulletin) {
        alert(`Aucun bulletin disponible pour le trimestre ${selectedTrimester}. Le bulletin est peut-être encore en cours de préparation.`);
        return;
      }

      console.log('📄 Téléchargement du bulletin:', bulletin);

      const response = await fetch(`http://127.0.0.1:8000/api/v1/bulletins/telecharger/${bulletin.id}`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bulletin_T${selectedTrimester}_2025-2026_${studentInfo?.prenom}_${studentInfo?.nom}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        console.log('✅ Bulletin téléchargé avec succès');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Erreur serveur:', errorData);
        alert('Erreur lors du téléchargement du bulletin. Veuillez réessayer plus tard.');
      }
    } catch (error) {
      console.error('❌ Erreur téléchargement:', error);
      alert('Erreur de connexion lors du téléchargement du bulletin.');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'absence': return '🚫';
      case 'retard': return '⏰';
      case 'note': return '📉';
      case 'note_faible': return '📉';
      case 'convocation': return '📞';
      case 'paiement': return '💰';
      case 'bulletin': return '📄';
      case 'info': return '📢';
      default: return '📢';
    }
  };

  const getNotificationColor = (priority) => {
    switch (priority) {
      case 'critical': return '#DC3545';
      case 'haute': return '#FF6B35';
      case 'high': return '#FF6B35';
      case 'medium': return '#F39C12';
      case 'normale': return '#3498DB';
      case 'low': return '#3498DB';
      default: return '#6C757D';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'critical': return 'URGENT';
      case 'haute': return 'Important';
      case 'high': return 'Important';
      case 'medium': return 'Moyen';
      case 'normale': return 'Info';
      case 'low': return 'Info';
      default: return '';
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const response = await fetch(`http://127.0.0.1:8000/api/v1/admin/notifications/${notificationId}/marquer-lu`, {
        method: 'POST'
      });
      
      if (response.ok) {
        setNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
        );
        console.log('✅ Notification marquée comme lue:', notificationId);
      }
    } catch (error) {
      console.error('❌ Erreur lors du marquage comme lu:', error);
    }
  };

  // Vérifier si un bulletin est disponible pour le trimestre sélectionné
  const bulletinDisponible = bulletins.find(b => 
    b.trimestre === selectedTrimester && 
    b.annee_scolaire === '2025-2026' && 
    b.statut === 'genere'
  );

  const unreadCount = notifications.filter(n => !n.lu).length;

  if (loading) {
    return (
      <div className="admin-layout">
        <DashboardHeader />
        <ParentNavigationBar 
          title={toSentenceCase("Mon suivi scolaire")} 
          showBack={false}
        />
        <div className="admin-container">
          <main className="parent-main admin-content-full">
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Chargement de vos informations...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <ParentNavigationBar 
        title="Suivi Scolaire" 
        showBack={false}
      />
      <div className="admin-container">
        <main className="parent-main admin-content-full">
          <div className="parent-dashboard">
            
            {/* Bannière de bienvenue Épurée - Espace Élève */}
            <div className="portal-hero-banner">
              <div className="portal-hero-main">
                <span className="portal-hero-label">Portail Élève</span>
                
                <h1 className="portal-hero-title">
                  Bonjour <span className="portal-hero-name">{studentInfo?.prenom} {studentInfo?.nom}</span>
                </h1>

                <p className="portal-hero-subtitle">
                  Classe de <strong className="portal-hero-highlight">{studentInfo?.classe || '6ème A'}</strong> • Année Scolaire 2025-2026
                </p>

                <div className="portal-hero-date">
                  <CalendarIcon size={14} color="#94a3b8" /> {new Date().toLocaleDateString('fr-FR', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </div>
              </div>

              <div className="portal-hero-actions">
                <div className="portal-avatar-circle">
                  {((studentInfo?.prenom?.[0] || 'E') + (studentInfo?.nom?.[0] || 'L')).toUpperCase()}
                </div>
                <div className="trimester-select-wrapper">
                  <label htmlFor="student-trimester-select">{toSentenceCase("Trimestre")}:</label>
                  <select 
                    id="student-trimester-select"
                    value={selectedTrimester} 
                    onChange={(e) => setSelectedTrimester(e.target.value)}
                  >
                    <option value="1">{toSentenceCase("Trimestre 1")}</option>
                    <option value="2">{toSentenceCase("Trimestre 2")}</option>
                    <option value="3">{toSentenceCase("Trimestre 3")}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Statistiques rapides */}
            <div className="stats-overview">
              <div className="stat-card-parent" style={{ borderLeftColor: '#DC3545' }}>
                <div className="stat-icon" style={{ backgroundColor: '#DC3545' }}>
                  <CalendarIcon size={24} color="#fff" />
                </div>
                <div className="stat-content">
                  <h3>{stats.absences}</h3>
                  <p>{toSentenceCase("Absences")}</p>
                </div>
              </div>
              <div className="stat-card-parent" style={{ borderLeftColor: '#F39C12' }}>
                <div className="stat-icon" style={{ backgroundColor: '#F39C12' }}>
                  <ClockIcon size={24} color="#fff" />
                </div>
                <div className="stat-content">
                  <h3>{stats.retards}</h3>
                  <p>{toSentenceCase("Retards")}</p>
                </div>
              </div>
              <div className="stat-card-parent" style={{ borderLeftColor: stats.moyenne >= 10 ? '#27AE60' : '#DC3545' }}>
                <div className="stat-icon" style={{ backgroundColor: stats.moyenne >= 10 ? '#27AE60' : '#DC3545' }}>
                  <ChartIcon size={24} color="#fff" />
                </div>
                <div className="stat-content">
                  <h3>{stats.moyenne}/20</h3>
                  <p>{toSentenceCase("Moyenne générale")}</p>
                </div>
              </div>
              <div className="stat-card-parent" style={{ borderLeftColor: '#FF6B35' }}>
                <div className="stat-icon" style={{ backgroundColor: '#FF6B35' }}>
                  <AlertIcon size={24} color="#fff" />
                </div>
                <div className="stat-content">
                  <h3>{stats.notes_faibles}</h3>
                  <p>{toSentenceCase("Notes faibles")}</p>
                </div>
              </div>
            </div>

            {/* Section Notifications */}
            <div className="notifications-section-parent">
              <div className="section-header-parent">
                <h3>
                  <BellIcon size={24} color="#0078D7" />
                  {toSentenceCase("Notifications du trimestre")} {selectedTrimester}
                </h3>
                {unreadCount > 0 && (
                  <span className="unread-badge">{unreadCount} {toSentenceCase("non lue")}{unreadCount > 1 ? 's' : ''}</span>
                )}
              </div>

              {notifications.length === 0 ? (
                <div className="empty-notifications">
                  <div style={{ marginBottom: '16px' }}>
                    <CheckCircleIcon size={48} color="#27AE60" />
                  </div>
                  <p>{toSentenceCase("Aucune notification pour ce trimestre")}</p>
                  <span>{toSentenceCase("Tout est à jour")}</span>
                </div>
              ) : (
                <div className="notifications-list-parent">
                  {notifications.map(notification => (
                    <div 
                      key={notification.id} 
                      className={`notification-card ${notification.read ? 'read' : 'unread'}`}
                      onClick={() => markAsRead(notification.id)}
                    >
                      <div className="notification-icon-wrapper" style={{ backgroundColor: getNotificationColor(notification.priority) + '20' }}>
                        <BellIcon size={20} color={getNotificationColor(notification.priority)} />
                      </div>
                      <div className="notification-content">
                        <div className="notification-header-row">
                          <h4>{notification.title}</h4>
                          <span 
                            className="priority-badge" 
                            style={{ backgroundColor: getNotificationColor(notification.priority) }}
                          >
                            {getPriorityLabel(notification.priority)}
                          </span>
                        </div>
                        <p className="notification-message">{notification.message}</p>
                        <span className="notification-date">
                          <CalendarIcon size={14} />
                          {new Date(notification.date).toLocaleDateString('fr-FR', { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric' 
                          })}
                        </span>
                      </div>
                      {!notification.read && <div className="unread-indicator"></div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Section Notes */}
            {notes.length > 0 && (
              <div className="notes-section-parent">
                <div className="section-header-parent">
                  <h3>
                    <BookIcon size={24} color="#0078D7" />
                    {toSentenceCase("Mes notes du trimestre")} {selectedTrimester}
                  </h3>
                  <button 
                    className="toggle-notes-btn"
                    onClick={() => setShowNotes(!showNotes)}
                  >
                    {showNotes ? 'Masquer' : 'Afficher'}
                  </button>
                </div>

                {showNotes && (
                  <div className="notes-grid">
                    {notes.map(note => (
                      <div key={note.id} className="note-card">
                        <div className="note-header">
                          <h4>{note.matiere}</h4>
                          <span className={`note-value ${note.note < 10 ? 'note-faible' : note.note >= 14 ? 'note-bonne' : ''}`}>
                            {note.note}/20
                          </span>
                        </div>
                        <div className="note-details">
                          <span className="note-coef">Coef. {note.coefficient}</span>
                          <span className="note-type">{note.type}</span>
                        </div>
                        {note.observation && (
                          <p className="note-observation">{note.observation}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Actions rapides */}
            <div className="quick-actions-parent">
              <h3>{toSentenceCase("Actions rapides")}</h3>
              <div className="actions-grid">
                {/* Bouton de téléchargement de bulletin - affiché seulement si disponible */}
                {bulletinDisponible && (
                  <button className="action-card bulletin-available" onClick={handleDownloadBulletin}>
                    <BookIcon size={32} color="#27AE60" />
                    <span>{toSentenceCase("Télécharger bulletin")}</span>
                    <small className="bulletin-info">Trimestre {selectedTrimester} disponible</small>
                  </button>
                )}
                
                {/* Message si pas de bulletin disponible */}
                {!bulletinDisponible && (
                  <div className="action-card bulletin-unavailable">
                    <BookIcon size={32} color="#6c757d" />
                    <span>{toSentenceCase("Bulletin non disponible")}</span>
                    <small className="bulletin-info">Trimestre {selectedTrimester} en cours</small>
                  </div>
                )}
                
                <button className="action-card" onClick={() => window.navigateTo && window.navigateTo('student-notes')}>
                  <FileTextIcon size={32} color="#0078D7" />
                  <span>{toSentenceCase("Voir mes notes")}</span>
                </button>
                <button className="action-card" onClick={() => window.navigateTo && window.navigateTo('contact-lycee')}>
                  <MailIcon size={32} color="#F39C12" />
                  <span>{toSentenceCase("Contacter l'école")}</span>
                </button>
                <button className="action-card" onClick={() => setShowSignalement(true)}>
                  <ShieldIcon size={32} color="#DC3545" />
                  <span>{toSentenceCase("Signaler un incident")}</span>
                </button>
              </div>
            </div>

            {/* Modal de signalement */}
            {showSignalement && (
              <SignalementIncident 
                onClose={() => setShowSignalement(false)}
                onSuccess={(data) => {
                  console.log('Signalement créé:', data);
                }}
              />
            )}

          </div>
        </main>
      </div>
    </div>
  );
}

export default StudentDashboard;
